import { cn } from "@/lib/utils";
import type { ReportReview } from "@/features/credentials/components/primary-source-runs/run-report-dialog/use-report-review";
import type {
  ReportItem,
  RunReport,
  RunScope,
} from "@/features/credentials/components/primary-source-runs/types";
import { REPORT_TABLE_COLUMNS } from "./report-table-columns";
import { ReportTableRow } from "./report-table-row";

const COLUMN_HEADINGS = [
  "Verification type",
  "Result",
  "Verified on",
  "Verified by",
  "Source",
];

/** The run's items, one row each. Rows come from the caller so a section view
 * and the overview share the same table. */
export function ReportTable({
  items,
  data,
  scope,
  canEdit,
  review,
  focusKey,
  onOpen,
  onFocused,
}: {
  items: ReportItem[];
  data: RunReport;
  scope: RunScope;
  canEdit: boolean;
  review: ReportReview;
  /** The item to bring into view once, as the one opened from the overview. */
  focusKey: string | null;
  /** Set on the overview: a row opens its item's section instead of
   * expanding in place. */
  onOpen?: (item: ReportItem) => void;
  onFocused: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div
        aria-hidden="true"
        className={cn(
          "hidden gap-x-4 border-b bg-muted/40 px-4 py-2.5 text-xs font-semibold text-muted-foreground sm:grid",
          REPORT_TABLE_COLUMNS,
        )}
      >
        {COLUMN_HEADINGS.map((heading) => (
          <span key={heading}>{heading}</span>
        ))}
        <span />
      </div>
      {items.map((item) => (
        <ReportTableRow
          key={item.key}
          item={item}
          checks={data.checks}
          runId={data.run.id}
          scope={scope}
          expanded={review.isExpanded(item)}
          canEdit={canEdit}
          decisions={review.decisions}
          uploadFocusRequested={review.uploadFocusKey === item.key}
          focusRequested={focusKey === item.key}
          onToggle={() => review.toggle(item)}
          onOpen={onOpen && (() => onOpen(item))}
          onFocused={onFocused}
          onUploadFocused={review.clearUploadFocus}
          onAccept={() => review.accept(item)}
          onConcern={() => review.concern(item)}
          onUndo={() => review.undo(item)}
        />
      ))}
    </div>
  );
}
